
import React, { useState } from "react";
import { AdminLayout } from "@/components/admin/AdminLayout";
import { PageHeaderCard } from "@/components/layout/PageHeaderCard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Calendar, DollarSign, AlertTriangle } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export default function AdminBilling() {
  const [searchTerm, setSearchTerm] = useState("");

  const { data: subscriptions, isLoading } = useQuery({
    queryKey: ['admin-billing', searchTerm],
    queryFn: async () => {
      const query = supabase
        .from('company_subscriptions')
        .select(`
          *,
          empresas(razao_social, nome_fantasia),
          subscription_plans(name, price)
        `);

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching billing data:', error);
        return [];
      }

      return data || [];
    }
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'expired':
        return 'bg-red-100 text-red-800';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Ativo';
      case 'expired':
        return 'Expirado';
      case 'suspended':
        return 'Suspenso';
      default:
        return 'Inativo';
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  };

  const isExpiringSoon = (expiresAt: string) => {
    const expirationDate = new Date(expiresAt);
    const now = new Date();
    const daysUntilExpiration = Math.ceil((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return daysUntilExpiration <= 7 && daysUntilExpiration > 0;
  };

  const isOverdue = (expiresAt: string) => {
    const expirationDate = new Date(expiresAt);
    const now = new Date();
    return expirationDate < now;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <PageHeaderCard
          eyebrow="Administração"
          title="Cobranças"
          subtitle="Gerencie pagamentos e assinaturas das empresas."
          backTo="/admin/control-center"
          search={{
            value: searchTerm,
            onChange: setSearchTerm,
            placeholder: "Buscar assinaturas...",
          }}
        />

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card className="rounded-2xl border-border/60 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
                  <DollarSign className="h-4 w-4 text-violet-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Receita Mensal</p>
                  <p className="text-lg font-semibold tabular-nums text-foreground">
                    {formatPrice(subscriptions?.reduce((sum, sub) =>
                      sum + (sub.subscription_plans?.price || 0), 0) || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/60 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
                  <Calendar className="h-4 w-4 text-violet-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Assinaturas Ativas</p>
                  <p className="text-lg font-semibold tabular-nums text-foreground">
                    {subscriptions?.filter(sub => sub.status === 'active').length || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border/60 shadow-sm">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
                  <AlertTriangle className="h-4 w-4 text-violet-600" />
                </div>
                <div className="min-w-0">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Vencendo em Breve</p>
                  <p className="text-lg font-semibold tabular-nums text-foreground">
                    {subscriptions?.filter(sub => sub.expires_at && isExpiringSoon(sub.expires_at)).length || 0}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="rounded-2xl border-border/60 shadow-sm">
          <CardContent className="pt-6">
            {isLoading ? (
              <div className="flex justify-center py-8">
                <div className="text-gray-500">Carregando cobranças...</div>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empresa</TableHead>
                    <TableHead>Plano</TableHead>
                    <TableHead>Valor</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Iniciado em</TableHead>
                    <TableHead>Expira em</TableHead>
                    <TableHead>Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subscriptions?.map((subscription) => (
                    <TableRow key={subscription.id}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{subscription.empresas?.razao_social || 'Não informado'}</div>
                          {subscription.empresas?.nome_fantasia && (
                            <div className="text-sm text-gray-500">{subscription.empresas.nome_fantasia}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{subscription.subscription_plans?.name || 'Não informado'}</TableCell>
                      <TableCell>{formatPrice(subscription.subscription_plans?.price || 0)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Badge className={getStatusColor(subscription.status)}>
                            {getStatusText(subscription.status)}
                          </Badge>
                          {subscription.expires_at && isExpiringSoon(subscription.expires_at) && (
                            <AlertTriangle className="w-4 h-4 text-yellow-500" />
                          )}
                          {subscription.expires_at && isOverdue(subscription.expires_at) && (
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {subscription.started_at ? new Date(subscription.started_at).toLocaleDateString('pt-BR') : 'Não informado'}
                      </TableCell>
                      <TableCell>
                        {subscription.expires_at ? new Date(subscription.expires_at).toLocaleDateString('pt-BR') : 'Não informado'}
                      </TableCell>
                      <TableCell>
                        <Button variant="outline" size="sm">
                          Gerenciar
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
